/**
 * GoldMaterial — Echtzeit-Gold fuer Karten-Schmuckelemente (Ortsnamen,
 * Track-Embleme).
 *
 * Metall lebt ausschliesslich von dem, was es SPIEGELT — eine gleichmaessig
 * helle Env macht Gold creme. Deshalb eine einmalige prozedurale
 * Juwelier-Studio-Umgebung: dunkler Raum, wenige heisse Panels
 * (HDR-Farbwerte > 1), ein kuehles Gegenlicht fuer die Kanten. PMREM einmal
 * pro Renderer (~10 ms), kein Netz-Asset; die Szenen-Beleuchtung bleibt
 * unberuehrt. Quell-Geometrien werden nach dem Bake freigegeben.
 */
import * as THREE from 'three';

let _envTex: THREE.Texture | null = null;
let _envRenderer: THREE.WebGLRenderer | null = null;
let _envRefs = 0;

function juwelierEnv(renderer: THREE.WebGLRenderer): THREE.Texture {
    if (_envTex && _envRenderer === renderer) return _envTex;
    const env = new THREE.Scene();
    const teile: Array<THREE.Mesh> = [];
    const panel = (farbe: number, staerke: number, w: number, h: number,
                   x: number, y: number, z: number): void => {
        const m = new THREE.Mesh(
            new THREE.PlaneGeometry(w, h),
            new THREE.MeshBasicMaterial({ color: new THREE.Color(farbe).multiplyScalar(staerke), side: THREE.DoubleSide }),
        );
        m.position.set(x, y, z);
        m.lookAt(0, 0, 0);
        env.add(m);
        teile.push(m);
    };
    const raum = new THREE.Mesh(
        new THREE.BoxGeometry(40, 40, 40),
        new THREE.MeshBasicMaterial({ color: 0x0b0a08, side: THREE.BackSide }),
    );
    env.add(raum);
    teile.push(raum);
    panel(0xfff1d8, 14, 12, 5, 0, 12, 8);   // warmes Key-Panel schraeg oben
    panel(0xffd9a0, 6, 5, 10, -12, 4, -4);  // seitliches Warmlicht
    panel(0xbcd4ff, 4, 8, 3, 10, 2, -9);    // kuehles Gegenlicht (Kanten)
    panel(0xffffff, 2, 20, 1, 0, -6, 10);   // schmaler Bodenstreifen

    /* sigmaRadians 0.04 statt 0.05: three deckelt den Blur bei 20 Samples,
     * 0.05 forderte 25 an und wurde geclippt (Konsolen-Warnung). */
    const pmrem = new THREE.PMREMGenerator(renderer);
    _envTex = pmrem.fromScene(env, 0.04).texture;
    _envRenderer = renderer;
    pmrem.dispose();
    for (const t of teile) {
        t.geometry.dispose();
        (t.material as THREE.Material).dispose();
    }
    return _envTex;
}

export function erzeugeGoldMaterial(renderer: THREE.WebGLRenderer, roughness = 0.26): THREE.MeshStandardMaterial {
    _envRefs++;
    return new THREE.MeshStandardMaterial({
        color: new THREE.Color().setRGB(1.0, 0.68, 0.24, THREE.LinearSRGBColorSpace),
        metalness: 1.0,
        roughness,
        envMap: juwelierEnv(renderer),
        envMapIntensity: 1.35,
    });
}

/** Gegenstueck zu erzeugeGoldMaterial: Material freigeben, die gemeinsam
 *  gecachte EnvMap erst mit dem LETZTEN Besitzer (Refcount) — Ortsnamen
 *  duerfen sie nicht disposen, solange Track-Embleme sie weiterverwenden. */
export function gebeGoldMaterialFrei(material: THREE.MeshStandardMaterial): void {
    material.dispose();
    _envRefs = Math.max(0, _envRefs - 1);
    if (_envRefs === 0) {
        _envTex?.dispose();
        _envTex = null;
        _envRenderer = null;
    }
}

